// @ts-nocheck
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { useOutreachData, useEventTeams, useSentHistory, OutreachRecipient } from './hooks/useOutreachData';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Send, Eye, ChevronDown, ChevronUp, CalendarIcon, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import DOMPurify from 'dompurify';
import TiptapEditor from './TiptapEditor';

const TEMPLATES = [
  {
    key: 'initial_reminder',
    label: 'Initial Hotel Reminder',
    subject: 'Hotel Booking Reminder — {event_name}',
    body: `<p>Hi {coach_name},</p>
<p>This is a friendly reminder that hotel accommodations for <strong>{event_name}</strong> are available through our official hotel partner.</p>
<p>Please book your team's rooms using the link below:</p>
<p><a href="{booking_url}">{booking_url}</a></p>
<p>Group rate expires: <strong>{group_rate_expiry}</strong></p>
<p>Thank you!</p>`,
  },
  {
    key: 'last_chance',
    label: 'Last Chance',
    subject: 'LAST CHANCE: Hotel Rate Expires Soon — {event_name}',
    body: `<p>Hi {coach_name},</p>
<p>The group rate for <strong>{event_name}</strong> expires on <strong>{group_rate_expiry}</strong>. After that, standard rates apply.</p>
<p>Book now: <a href="{booking_url}">{booking_url}</a></p>
<p>Don't miss out!</p>`,
  },
  {
    key: 'custom',
    label: 'Custom Message',
    subject: '',
    body: '<p>Hi {coach_name},</p><p></p>',
  },
];

const MERGE_TAGS = ['{coach_name}', '{team_name}', '{event_name}', '{booking_url}', '{group_rate_expiry}'];

const HotelOutreach: React.FC = () => {
  const location = useLocation();
  const qc = useQueryClient();
  const { assignedEventsQuery } = useOutreachData();
  const assignedEvents = assignedEventsQuery.data || [];

  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [templateKey, setTemplateKey] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Pre-select event from navigation state (Team Tracker "Send Reminder")
  useEffect(() => {
    const stateEventId = (location.state as any)?.eventId;
    if (stateEventId) setSelectedEventId(stateEventId);
  }, [location.state]);

  const teamsQuery = useEventTeams(selectedEventId || null);
  const teams = teamsQuery.data || [];
  const assignedEventIds = useMemo(() => assignedEvents.map((e) => e.event_id), [assignedEvents]);
  const history = useSentHistory(assignedEventIds);

  const selectedEvent = assignedEvents.find((e) => e.event_id === selectedEventId);

  const handleTemplateSelect = useCallback((key: string) => {
    setTemplateKey(key);
    const tpl = TEMPLATES.find((t) => t.key === key);
    if (tpl) {
      setSubject(tpl.subject);
      setBodyHtml(tpl.body);
    }
  }, []);

  const toggleRecipient = (id: string) => {
    setSelectedRecipients((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Local teams (inside the event's exempt radius) never get hotel reminders.
  const excludedLocalCount = useMemo(
    () => teams.filter((t) => !t.has_booking && t.hotel_radius_exempt).length,
    [teams]
  );

  const filteredTeams = useMemo(() => {
    let list = teams;
    // Only show unbooked + pending teams, never local-exempt ones
    list = list.filter((t) => !t.has_booking && !t.hotel_radius_exempt);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (t) =>
          t.coach_name.toLowerCase().includes(q) ||
          t.team_name.toLowerCase().includes(q) ||
          t.coach_email.toLowerCase().includes(q)
      );
    }
    return list;
  }, [teams, searchQuery]);

  const selectAll = () => {
    if (selectedRecipients.size === filteredTeams.length) {
      setSelectedRecipients(new Set());
    } else {
      setSelectedRecipients(new Set(filteredTeams.map((t) => t.registration_id)));
    }
  };

  const resolveMergeTags = (text: string, team: OutreachRecipient) => {
    return text
      .replace(/\{coach_name\}/g, team.coach_name || 'Coach')
      .replace(/\{team_name\}/g, team.team_name)
      .replace(/\{event_name\}/g, selectedEvent?.event_name || '')
      .replace(/\{booking_url\}/g, selectedEvent?.booking_url || '')
      .replace(/\{group_rate_expiry\}/g, selectedEvent?.group_rate_expiry || 'TBD');
  };

  const previewHtml = useMemo(() => {
    if (!showPreview || filteredTeams.length === 0) return '';
    const sample = filteredTeams[0];
    return DOMPurify.sanitize(resolveMergeTags(bodyHtml, sample));
  }, [showPreview, bodyHtml, filteredTeams, selectedEvent]);

  const handleSend = async () => {
    if (!selectedEventId || !subject.trim() || selectedRecipients.size === 0) {
      toast.error('Select an event, enter a subject, and choose recipients');
      return;
    }

    setSending(true);
    try {
      const recipients = teams
        .filter((t) => selectedRecipients.has(t.registration_id) && !t.hotel_radius_exempt)
        .map((t) => ({
          registration_id: t.registration_id,
          coach_email: t.coach_email,
          coach_name: t.coach_name,
          team_name: t.team_name,
          event_name: selectedEvent?.event_name || '',
          booking_url: selectedEvent?.booking_url || '',
          group_rate_expiry: selectedEvent?.group_rate_expiry || '',
        }));

      const { data, error } = await invokeEdgeFunction('rle-hotel-send-outreach', {
        body: {
          event_id: selectedEventId,
          subject,
          body: bodyHtml,
          template_type: templateKey,
          recipients,
        },
      });

      if (error) throw error;
      if (data && data.ok === false) throw new Error(data.error?.message || 'Send failed');

      const result = data?.data ?? data;
      toast.success(`Sent ${result?.sent ?? selectedRecipients.size} emails`);
      if (result?.failed > 0) toast.error(`${result.failed} failed to send`);

      setSelectedRecipients(new Set());
      qc.invalidateQueries({ queryKey: ['hotel-outreach-history'] });
    } catch (err: any) {
      toast.error(err.message || 'Failed to send outreach');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Outreach Center</h1>

      {/* Event selector */}
      <div className="max-w-sm">
        <Select value={selectedEventId} onValueChange={setSelectedEventId}>
          <SelectTrigger>
            <SelectValue placeholder="Select an event..." />
          </SelectTrigger>
          <SelectContent>
            {assignedEvents.map((ev) => (
              <SelectItem key={ev.event_id} value={ev.event_id}>
                {ev.event_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedEventId && (
        <>
          {/* Template selector */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Template</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={templateKey} onValueChange={handleTemplateSelect}>
                <SelectTrigger className="max-w-sm">
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATES.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Subject + Body */}
          {templateKey && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Compose</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Subject</label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-medium">Body</label>
                  <div className="mt-1">
                    <TiptapEditor content={bodyHtml} onChange={setBodyHtml} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  <span className="text-xs text-muted-foreground mr-1">Merge tags:</span>
                  {MERGE_TAGS.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs cursor-default">{tag}</Badge>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)}>
                  <Eye className="h-4 w-4 mr-1" /> {showPreview ? 'Hide' : 'Show'} Preview
                </Button>
                {showPreview && previewHtml && (
                  <Card className="bg-muted/30">
                    <CardContent className="py-4">
                      <p className="text-xs text-muted-foreground mb-2">
                        Preview (using first recipient):
                      </p>
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                      />
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          )}

          {/* Recipient list */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">
                Recipients ({filteredTeams.length} unbooked)
              </CardTitle>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 w-48 text-xs"
                />
                <Button variant="outline" size="sm" onClick={selectAll}>
                  {selectedRecipients.size === filteredTeams.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {excludedLocalCount > 0 && (
                <p className="px-4 pt-3 text-xs text-muted-foreground">
                  {excludedLocalCount} local {excludedLocalCount === 1 ? 'team' : 'teams'} excluded —
                  they're within the event's local radius and don't need to book a hotel.
                </p>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" />
                    <TableHead>Coach</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamsQuery.isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 5 }).map((_, j) => (
                          <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filteredTeams.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                        All teams have bookings.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTeams.map((team) => (
                      <TableRow key={team.registration_id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedRecipients.has(team.registration_id)}
                            onCheckedChange={() => toggleRecipient(team.registration_id)}
                          />
                        </TableCell>
                        <TableCell className="text-sm">{team.coach_name}</TableCell>
                        <TableCell className="text-sm">{team.team_name}</TableCell>
                        <TableCell className="text-sm">{team.coach_email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs capitalize">
                            {team.booking_status || 'unbooked'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Send */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSend}
              disabled={sending || selectedRecipients.size === 0 || !subject.trim()}
            >
              <Send className="h-4 w-4 mr-2" />
              {sending ? 'Sending...' : `Send to ${selectedRecipients.size} recipient(s)`}
            </Button>
          </div>
        </>
      )}

      {/* Sent history */}
      <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer flex flex-row items-center justify-between">
              <CardTitle className="text-base">Sent History</CardTitle>
              {historyOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                <Select value={history.eventFilter} onValueChange={history.setEventFilter}>
                  <SelectTrigger className="h-8 w-48 text-xs">
                    <SelectValue placeholder="All events" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All events</SelectItem>
                    {assignedEvents.map((ev) => (
                      <SelectItem key={ev.event_id} value={ev.event_id}>{ev.event_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs">
                      <CalendarIcon className="h-3 w-3 mr-1" />
                      {history.dateFrom ? format(history.dateFrom, 'MMM d') : 'From'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={history.dateFrom} onSelect={history.setDateFrom} />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs">
                      <CalendarIcon className="h-3 w-3 mr-1" />
                      {history.dateTo ? format(history.dateTo, 'MMM d') : 'To'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={history.dateTo} onSelect={history.setDateTo} />
                  </PopoverContent>
                </Popover>

                {(history.dateFrom || history.dateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => { history.setDateFrom(undefined); history.setDateTo(undefined); }}
                  >
                    <X className="h-3 w-3 mr-1" /> Clear
                  </Button>
                )}
              </div>

              {/* History table */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Recipients</TableHead>
                      <TableHead>Sent By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.query.isLoading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 5 }).map((_, j) => (
                            <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : !history.query.data || history.query.data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                          No outreach sent yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      history.query.data.map((log: any) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-sm">{log.events?.name || '—'}</TableCell>
                          <TableCell className="text-sm">{log.subject}</TableCell>
                          <TableCell className="text-sm">
                            {log.sent_at ? format(parseISO(log.sent_at), 'MMM d, yyyy h:mm a') : '—'}
                          </TableCell>
                          <TableCell className="text-sm">{log.recipient_count}</TableCell>
                          <TableCell className="text-sm">
                            {log.sender
                              ? [log.sender.first_name, log.sender.last_name].filter(Boolean).join(' ') || log.sender.name || '—'
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default HotelOutreach;
